import { z } from "zod";

const moneyType = z.object({
  amount: z.string().or(z.number()),
});

const paymentItem = z.object({
  id: z.string(),
  href: z.string().optional(),
  originalPaymentItemId: z.string().optional(),
  originalOfferId: z.string().optional(),
  refundedAmount: moneyType.optional(),
  referredType: z.enum(["PRODUCT", "PAYMENT_BUNDLE_ITEM"]).optional(),
  productId: z.string().optional(),
  productOfferingId: z.string(),
  icon: z.string().optional(),
  name: z.string().optional(),
  description: z.string().optional(),
  status: z
    .enum(["NEW", "PENDING", "COMPLETED", "FAILED", "CANCELLED"])
    .optional(),
  sequenceId: z.number(),
  obligatory: z.string().optional(),
  amount: moneyType,
  invoice: z
    .object({
      invoiceId: z.string(),
      invoiceNumber: z.string(),
      invoiceItemId: z.string().optional(),
      taxRate: z.string().optional(),
    })
    .optional(),
});

export const status = z.enum([
  "NEW",
  "PENDING",
  "PENDING_REPEATING",
  "PENDING_REPEATING_RETRY",
  "COMPLETED",
  "FAILED",
  "PARTIAL",
  "CANCELLED",
]);

const channel = z.object({
  id: z.string(),
  href: z.string().optional(),
  referredType: z.string().optional(),
  name: z.string().optional(),
  description: z.string().optional(),
});

const relatedParty = z.object({
  id: z.string(),
  href: z.string().optional(),
  email: z.string().optional(),
  migrated: z.boolean().optional(),
});
export const paymentAction = z.enum([
  "ONE_CLICK",
  "RECURRENT",
  "REGISTER",
  "REGISTER_EXCLUSIVE",
  "DEACTIVATE",
  "REFUND",
  "SWITCH",
  "DUE",
  "POSTPAID",
]);
const paymentMethod = z.object({
  id: z.string().optional(),
  href: z.string().optional(),
  validFor: z
    .object({
      startDateTime: z.string(),
      endDateTime: z.string(),
    })
    .optional(),
  mode: z
    .enum(["PRIMARY", "SECONDARY", "REPLACE", "SEQUENCE", "HIDDEN"])
    .optional(),
  methodType: z
    .enum([
      "CREDIT_CARD",
      "BLIK",
      "DIGITAL_WALLET",
      "BANK_TRANSFER",
      "PAY_BY_LINK",
      "ADMIN",
      "BANK_ACCOUNT_EKLE",
    ])
    .optional(),
  authCode: z.string().optional(),
  bankAccountNb: z.string().optional(),
  receiverName: z.string().optional(),
  action: paymentAction,
  maskedCardNumber: z.string().optional(),
});

export const paymentType = z.enum(["REGISTER", "PAYMENT", "REFUND"]);

export const errorCategory = z
  .enum([
    "CONNECTION_ERROR",
    "CARD_LIMIT_EXCEEDED",
    "SECURITY_ERROR",
    "DO_NOT_HONOR",
    "CARD_EXPIRED",
    "BANK_DECLINED",
    "PICK_UP_CARD",
    "NOT_SUPPORTED",
    "DUPLICATE",
    "INVALID_TRANSACTION_DATA",
    "INCORRECT_CARD_NUMBER",
    "FRAUD_SUSPECT",
    "SYSTEM_ERROR",
    "STOP_RECURRING",
    "VOID",
    "UNCLASSIFIED",
    "PIN",
    "NUM_OF_FAILED_ATTEMPTS_EXCEEDED",
    "CRITICAL_SYSTEM_ERROR",
    "TEMPORARY_UNAVAILABLE_RETRY",
    "TIMEOUT",
    "GENERAL_ERROR",
    "CLOSED_ON_REQUEST",
    "REJECTED_BY_USER",
    "NO_VALID_PAYMENT_METHOD",
    "PAYMENT_LATE_CONFIRMATION",
    "DW_LIMIT_EXCEEDED",
  ])
  .optional();

export const payment = z.object({
  id: z.string(),
  href: z.string().optional(),
  type: paymentType,
  status,
  transactionLink: z.string().optional(),
  transactionBundleFlag: z.boolean().optional(),
  transactionBundleId: z.string().optional(),
  transactionBundleSize: z.number().optional(),
  transactionBundleTimeout: z.number().optional(),
  transactionTimeout: z.number().optional(),
  transactionType: z.enum(["ALL_IN_ONE", "SPLIT", "TRANSACTION"]),
  transactionTimestamp: z.string(),
  errorCode: z.string().optional(),
  errorCategory,
  errorDescription: z.string().optional(),
  paymentItem: z.array(paymentItem).default([]),
  externalOrderId: z.string().optional(),
  caseId: z.string().optional(),
  originalPaymentId: z.string().optional(),
  originalPaymentTransactionId: z.string().optional(),
  invoiceNumber: z.string().optional(),
  totalAmount: moneyType.optional(),
  refundedAmount: moneyType.optional(),
  channel: channel.optional(),
  payer: relatedParty,
  paymentMethod: paymentMethod,
  isRepeatable: z.boolean().optional(),
  beneficiary: z
    .object({
      referredType: z.string().optional(),
      email: z.string().optional(),
      id: z.string().optional(),
      phoneNumber: z.string().optional(),
    })
    .optional(),
});

export const serverResponse = z.array(payment);
